import {
  TextField,
  InputAdornment,
  Stack,
  MenuItem,
  Select,
  useTheme,
  SelectChangeEvent,
  styled
} from '@mui/material';
import { useState } from 'react';
import Image from 'next/image';
import Typography from '@/components/Typography';
import useResponsive from '@/hooks/useResponsive';

interface CryptoInputProps {
  label: string;
  options: { name: string; logo: string }[];
  value: string | number; // Input value (controlled)
  onInputChange: (value: string | number) => void; // Callback to handle input change
  onSelectChange: (selected: string) => void; // Callback to handle selected crypto change
}

const CustomTextField = styled(TextField)(({ theme }) => ({
  '&& .MuiInputBase-root': {
    // Double '&&' increases specificity
    backgroundColor: '#F8FAFD',
    border: 'transparent',
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      // borderColor: '#D1D5DB',
      borderColor: 'transparent'
    },
    '&.Mui-focused': {
      borderColor: 'transparent',
      boxShadow: `0 0 0 2px rgba(59, 130, 246, 0.3)`
    }
  },
  '&& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent'
  }
}));

const CryptoInput = ({
  label,
  options,
  value,
  onInputChange,
  onSelectChange
}: CryptoInputProps) => {
  const theme = useTheme();
  const { matches } = useResponsive();
  const [selectedCrypto, setSelectedCrypto] = useState(options[0].name); // Default selected option

  // Handle cryptocurrency selection change
  const handleCryptoChange = (event: SelectChangeEvent<string>) => {
    const newCrypto = event.target.value as string;
    setSelectedCrypto(newCrypto);
    onSelectChange(newCrypto); // Call parent callback on crypto selection change
  };

  // Handle numeric input value change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onInputChange(event.target.value); // Pass the value up to the parent component
  };

  return (
    <Stack
      sx={{
        width: matches({ xs: '90%', lg: 341 }),
        backgroundColor: '#F8FAFD',
        paddingX: 4,
        paddingTop: 5,
        paddingBottom: 4,
        border: '1px solid #EFF2F5',
        borderRadius: 1
      }}
      gap={4}
    >
      <Typography variant="body3" sx={{ color: '#222531' }}>
        {label}
      </Typography>
      <CustomTextField
        variant="outlined"
        // type="number"
        value={value} // Controlled input value
        onChange={handleInputChange} // Handle input value change
        // sx={{
        //   '& .MuiInputBase-root': {
        //     backgroundColor: '#F8FAFD', // Custom background color
        //     borderColor: '#EFF2F5', // Custom border color for default state
        //     borderRadius: 1,
        //     '&:hover': {
        //       borderColor: theme.palette.primary.main, // Hover border color
        //     },
        //     '&.Mui-focused': {
        //       borderColor: theme.palette.primary.dark, // Focused border color
        //       boxShadow: `0 0 6px ${theme.palette.primary.main}`,
        //     },
        //   },
        //   '& .MuiTextField-root': {
        //     backgroundColor: '#333',
        //     border: 'none'
        //   },
        //   '& .MuiOutlinedInput-root': {
        //     backgroundColor: '#F8FAFD',
        //     '& fieldset': {
        //       borderColor: '#F8FAFD'
        //     },
        //     '&:hover fieldset': {
        //       borderColor: '#F8FAFD'
        //     },
        //     '&.Mui-focused fieldset': {
        //       borderColor: '#F8FAFD'
        //     }
        //   },
        //   input: {
        //     color: '#222531'
        //   }
        // }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Select
                value={selectedCrypto} // Controlled select value
                onChange={handleCryptoChange} // Handle cryptocurrency change
                sx={{
                  minWidth: 126,
                  backgroundColor: '#EFF2F5', // Custom background for select
                  color: '#222531',
                  border: '1px solid #EFF2F5',
                  borderRadius: 1.5,
                  '.MuiOutlinedInput-notchedOutline': {
                    border: 'none' // Remove border of the select
                  },
                  '.MuiSelect-select': {
                    paddingRight: 0, // Adjust padding to fit text
                    backgroundColor: '#EFF2F5'
                  },
                  '& .MuiPaper-root': {
                    backgroundColor: '#EFF2F5',
                    borderRadius: 2 // Dropdown menu background
                  },
                  '& .MuiList-root': {
                    backgroundColor: '#EFF2F5',
                    borderRadius: 2
                  }
                }}
              >
                {options.map(option => (
                  <MenuItem key={option.name} value={option.name}>
                    <Stack flexDirection="row" alignItems="center" gap={2}>
                      <Image
                        src={option.logo}
                        alt={option.name}
                        width={24}
                        height={24}
                      />
                      <Typography variant="body2" color="#222531">
                        {option.name}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </InputAdornment>
          )
        }}
      />
    </Stack>
  );
};

export default CryptoInput;
